import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { WebPageSchema, BreadcrumbListSchema } from '@/components/feature/SEOMeta';

const categoryArticleImages: Record<string, string> = {
  'season-1-cast-reveal': 'https://readdy.ai/api/search-image?query=Group%20of%20diverse%20attractive%20young%20Asian%20people%20posing%20on%20tropical%20beach%2C%20reality%20TV%20cast%20photo%2C%20cinematic%20lighting%2C%20warm%20vibrant%20colors&width=800&height=500&seq=cat-art-1&orientation=landscape',
  'island-location-guide': 'https://readdy.ai/api/search-image?query=Luxury%20private%20tropical%20island%20with%20beach%20villas%2C%20crystal%20clear%20turquoise%20water%2C%20palm%20trees%2C%20aerial%20view%2C%20cinematic%20lighting&width=800&height=500&seq=cat-art-2&orientation=landscape',
  'reality-dating-psychology': 'https://readdy.ai/api/search-image?query=Young%20Asian%20couple%20having%20deep%20conversation%20on%20beach%20at%20sunset%2C%20documentary%20style%2C%20cinematic%20lighting%2C%20emotional%20connection&width=800&height=500&seq=cat-art-3&orientation=landscape',
  'how-to-apply': 'https://readdy.ai/api/search-image?query=Young%20Asian%20person%20filling%20out%20application%20form%20with%20tropical%20beach%20background%2C%20hopeful%20expression%2C%20cinematic%20lighting&width=800&height=500&seq=cat-art-4&orientation=landscape',
  'asian-dating-culture': 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20young%20Asian%20couples%20from%20different%20countries%2C%20cultural%20diversity%2C%20tropical%20setting%2C%20cinematic%20lighting&width=800&height=500&seq=cat-art-5&orientation=landscape',
  'producer-interview': 'https://readdy.ai/api/search-image?query=TV%20producer%20being%20interviewed%20with%20tropical%20island%20backdrop%2C%20professional%20setting%2C%20cinematic%20lighting&width=800&height=500&seq=cat-art-6&orientation=landscape',
};

const defaultImg = 'https://readdy.ai/api/search-image?query=Asian%20reality%20TV%20show%20production%20scene%2C%20tropical%20island%20lifestyle%2C%20young%20diverse%20Asian%20people%20on%20beach%2C%20cinematic%20lighting&width=800&height=500&seq=cat-art-default&orientation=landscape';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];

  const filtered = useMemo(() => {
    if (activeSlug === 'all') return articles;
    return articles.filter((a) => a.category === activeCategory.name);
  }, [activeSlug, activeCategory]);

  useEffect(() => {
    document.title = `${activeCategory.name} - 爱情岛亚洲资讯中心`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `爱情岛亚洲${activeCategory.name}栏目，浏览最新的${activeCategory.name}相关资讯、文章和动态。`);
  }, [activeCategory]);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '资讯中心', to: '/category/all' },
    ...(activeSlug !== 'all' ? [{ label: activeCategory.name }] : []),
  ];

  const breadcrumbSchemaItems = [
    { name: '首页', item: 'https://www.aiqingdao.asia/' },
    { name: '资讯中心', item: 'https://www.aiqingdao.asia/category/all' },
    ...(activeSlug !== 'all' ? [{ name: activeCategory.name }] : []),
  ];

  return (
    <>
      <WebPageSchema
        title={`${activeCategory.name} - 爱情岛亚洲资讯中心`}
        description={`爱情岛亚洲${activeCategory.name}栏目，浏览最新的${activeCategory.name}相关资讯、文章和动态。`}
        url={`https://www.aiqingdao.asia/category/${activeSlug}`}
      />
      <BreadcrumbListSchema items={breadcrumbSchemaItems} />

      <div className="pt-20 md:pt-24 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />
          <div className="text-center py-8 md:py-12">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              资讯中心
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              了解爱情岛亚洲的最新动态、幕后故事和深度报道
            </p>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 pb-16">
              {filtered.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={categoryArticleImages[article.slug] || defaultImg}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                        {article.category}
                      </span>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{article.publishDate}</span>
                      <span>{article.author}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
              <Link to="/category/all" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600">
                查看全部文章
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
}